import React, { useEffect, useState } from 'react';
import {
  Typography, Card, Row, Col, Table, Spin, Alert, Divider
} from 'antd';
import { Link } from 'react-router-dom';
import {
  UserOutlined,
  CheckCircleOutlined,
  CalendarOutlined,
  BellOutlined
} from '@ant-design/icons';
import styled from 'styled-components';
import PersistentDrawerLeft from '../../reusable/admin/Main';
import { restApi } from "../../../constants/api";
import { render } from '@testing-library/react';
import dayjs from "dayjs";
import NoteCard from '../Notesdashboard/NoteCard'; 

const { Title, Text } = Typography;

const StyledDashboardContainer = styled.div`
  padding: 24px;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const MetricCard = styled(Card) <{ $gradient: string }>`
  background: ${({ $gradient }) => $gradient};
  color: white;
  border: none;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s;

  &:hover {
    transform: translateY(-6px);
  }

  .ant-card-body {
    padding: 24px;
  }
`;

const CardIcon = styled.div`
  font-size: 32px;
  margin-bottom: 12px;
  color: white;
`;

const CardTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: white;
`;

const CardValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: white;
`;

const StyledSection = styled.div`
  margin-top: 40px;

  .ant-table-wrapper {
    border-radius: 16px;
    overflow: hidden;
    background: white;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
  }

  .ant-table {
    border-radius: 16px;
    overflow: hidden;
  }

  .ant-table-thead > tr > th {
    background: linear-gradient(to right,rgb(200, 219, 236),rgb(213, 215, 216));
    color: black;
    font-weight: 600;
    font-size: 14px;
    padding: 16px;
  }

  .ant-table-tbody > tr:nth-child(odd) > td {
    background-color: #fafafa;
  }

  .ant-table-tbody > tr:hover > td {
    background-color: #e6f7ff !important;
    transition: background-color 0.3s ease;
  }

  .ant-table-tbody > tr > td {
    font-size: 14px;
    padding: 14px 16px;
    border-bottom: 1px solid #f0f0f0;
  }

  .ant-pagination {
    margin-top: 16px;
    justify-content: center;
  }
`;
const UpDownIcon = styled.span`
  display: inline-block;
  animation: upDown 1s infinite alternate;

  @keyframes upDown {
    0% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-5px);
    }
    100% {
      transform: translateY(0);
    }
  }
`;

const formatDate = (dateString: string) => {
  return dayjs(dateString).format('DD MMM YYYY').toUpperCase(); // e.g., 24 APR 2025
};
const Dashboard = () => {
  const [summary, setSummary] = useState<any>(null);
  const [reminders, setReminders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      let current_date = dayjs().format('YYYY-MM-DD')
      const summaryResponse: any = await restApi('/api/dashboard/summary', 'POST', {
        current_date
      });
      const remindersResponse: any = await restApi('/api/dashboard/reminders', 'POST', {
        current_date
      });

      setSummary(summaryResponse?.data);
      setReminders(remindersResponse?.data);
      setError(null);
    } catch (err: any) {
      setError('Failed to load dashboard data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const columns = [
    {
      title: 'User Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Designation',
      dataIndex: 'type_name',
      key: 'type_name',
    },
    {
      title: 'Department',
      dataIndex: 'department_name',
      key: 'department_name',
    },
    {
      title: 'Filled Days',
      dataIndex: 'filled_days',
      key: 'filled_days',
    },
    {
      title: 'Remaining Days',
      dataIndex: 'remaining_days',
      key: 'remaining_days',
      render: (text: any) => {
        return (<label style={{ color: "red", fontWeight: 700 }}>{text}</label>)
      }
    },
    {
      title: 'Start Date',
      dataIndex: 'start_date',
      key: 'start_date',
      render: (text: string) => formatDate(text),
    },
    {
      title: 'End Date',
      dataIndex: 'end_date',
      key: 'end_date',
      render: (text: string) => (
        <UpDownIcon >
          <div
            style={{
              backgroundColor: 'rgb(255 71 71)', // Light red background color
              padding: '8px 12px',
              color: "white",
              borderRadius: '8px',
              fontSize: '16px',
              display: 'inline-flex',
              alignItems: 'center',
            }}
          >
            <UpDownIcon
              style={{
                fontWeight: 700,
                fontSize: '18px',
              }}
            >
              ⚠️
            </UpDownIcon>
            {formatDate(text)}
          </div>
        </UpDownIcon>
      ),
    },
  ];

  const cards = [
    {
      title: 'Total Users',
      value: summary?.total_users,
      icon: <UserOutlined />,
      gradient: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
      link: '/admin/users',
    },
    {
      title: 'Active Cycles',
      value: summary?.active_cycles,
      icon: <CheckCircleOutlined />,
      gradient: 'linear-gradient(135deg, #42e695 0%, #3bb2b8 100%)',
      link: '/admin/users/cycles',
    },
    // {
    //   title: 'Today’s Attendance',
    //   value: summary?.today_attendance,
    //   icon: <CalendarOutlined />,
    //   gradient: 'linear-gradient(135deg, #f6d365 0%, #fda085 100%)',
    // },
    // {
    //   title: 'Due Reminders',
    //   value: summary?.due_reminders,
    //   icon: <BellOutlined />,
    //   gradient: 'linear-gradient(135deg, #f953c6 0%, #b91d73 100%)',
    // }
  ];

  return (
    <PersistentDrawerLeft>
      <StyledDashboardContainer>
        {loading ? (
          <Spin tip="Loading..." size="large" />
        ) : error ? (
          <Alert type="error" message={error} />
        ) : (
          <>
            <Row gutter={[24, 24]}>
              {cards.map((card, index) => (
                // <Link to={card.link} key={index} style={{ textDecoration: 'none' }}>
                //   <Col xs={24} sm={12} md={6} lg={12} xl={6} xxl={6} key={index}>
                //     <MetricCard $gradient={card.gradient ? card.gradient : ""}>
                //       <CardIcon>{card.icon}</CardIcon>
                //       <CardTitle>{card.title}</CardTitle>
                //       <CardValue>{card.value}</CardValue>
                //     </MetricCard>
                //   </Col>
                // </Link>
                <Col xs={24} sm={12} md={6} lg={12} xl={6} xxl={6} key={index}>
                  <Link to={card.link} style={{ textDecoration: 'none' }}>
                    <MetricCard $gradient={card.gradient ? card.gradient : ""}>
                      <CardIcon>{card.icon}</CardIcon>
                      <CardTitle>{card.title}</CardTitle>
                      <CardValue>{card.value}</CardValue>
                    </MetricCard>
                  </Link>
                </Col>

              ))}
            </Row>

            <StyledSection>
              <Divider orientation="left" plain>
                <Text strong style={{ fontSize: 16 }}>Reminder Alerts</Text>
              </Divider>
              <Table
                dataSource={reminders}
                columns={columns}
                rowKey="user_id"
                pagination={{ pageSize: 10 }}
              />
            </StyledSection>
            <NoteCard />
          </>
        )}
      </StyledDashboardContainer>
    </PersistentDrawerLeft>
  );
};

export default Dashboard;
